import { Select, ToggleButtonGroup } from '@mui/material'
import { styled } from '@mui/material/styles'
import { type ReactNode, type SVGProps, useEffect, useMemo } from 'react'
import {
  Button as AriaButton,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Popover as AriaPopover,
  Select as AriaSelect,
  SelectValue as AriaSelectValue,
  Tab as AriaTab,
  TabList as AriaTabList,
  Tabs as AriaTabs,
  type Key,
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import IcBaselineAssistant from '~icons/ic/baseline-assistant'
import IcBaselineAutoAwesome from '~icons/ic/baseline-auto-awesome'
import IcBaselineFavorite from '~icons/ic/baseline-favorite'
import IcBaselineFavoriteBorder from '~icons/ic/baseline-favorite-border'
import IcBaselineLocalFireDepartment from '~icons/ic/baseline-local-fire-department'
import IcBaselinePeople from '~icons/ic/baseline-people'
import IcBaselinePeopleOutline from '~icons/ic/baseline-people-outline'
import IcBaselineSmartToy from '~icons/ic/baseline-smart-toy'
import IcBaselineTrendingUp from '~icons/ic/baseline-trending-up'
import IcBaselineWhatshot from '~icons/ic/baseline-whatshot'
import IconParkOutlineDown from '~icons/icon-park-outline/down'
import MdiSortAscending from '~icons/mdi/sort-ascending'
import MdiSortDescending from '~icons/mdi/sort-descending'
import { useArtworkListPageContext } from 'src/pages/AppPage/ArtworkListPageContext'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { OrderOption } from 'src/services/helpers/orderOptionParams'
import { cn, cx } from 'src/utils/cx'
import { extractTextFromNode } from 'src/utils/jsx'
import { useLocation } from 'src/utils/router/route'
import { useQs } from '../utils/qs'
import { useHash } from '../utils/router/useHash'
import { feedToolbarButtonStyles } from './ui/FeedToolbarButton'

// TODO: These two styled components are not being used here.
//       Move or remove them.

export const StyledToggleButtonGroup = styled(ToggleButtonGroup)(
  ({ theme }) => ({
    flexWrap: 'wrap',
    '& .MuiToggleButtonGroup-grouped': {
      border: 0,
      padding: '8px 16px',
      '&.Mui-disabled': {
        border: 0,
      },
      '&:not(:first-of-type)': {
        borderRadius: '9999px',
      },
      '&:first-of-type': {
        borderRadius: '9999px',
      },
    },
  }),
)

export const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: 'transparent',
  borderRadius: 'inherit',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    gap: '4px',
    fontWeight: 400,
  },

  '& .Mui-selected': {
    fontWeight: 400,
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
  '& .MuiSelect-icon': {
    color: 'inherit',
  },
}))

export const defaultChoicesForHome: string[] = [
  OrderOption.recommended,
  OrderOption.random,
  OrderOption.dailyRanking,
  OrderOption.followingNews,
  OrderOption.newest,
]

const defaultChoices: string[] = [
  OrderOption.random,
  OrderOption.dailyRanking,
  OrderOption.followingNews,
  OrderOption.mostLiked,
  OrderOption.newest,
]

const orderIconMap: Record<
  string,
  (props: { active: boolean } & SVGProps<SVGSVGElement>) => ReactNode
> = {
  [OrderOption.dailyRanking]: ({ active, className, ...rest }) => (
    <IcBaselineWhatshot
      className={cx(className, {
        'text-orange-600': active,
      })}
      {...rest}
    />
  ),
  [OrderOption.followingNews]: ({ active, className, ...rest }) =>
    active ? (
      <IcBaselinePeople className={cx('text-blue-600', className)} {...rest} />
    ) : (
      <IcBaselinePeopleOutline className={className} {...rest} />
    ),
  [OrderOption.mostLiked]: ({ active, className, ...rest }) =>
    active ? (
      <IcBaselineFavorite className={cx('text-red-500', className)} {...rest} />
    ) : (
      <IcBaselineFavoriteBorder className={className} {...rest} />
    ),
  [OrderOption.random]: ({ active, className, ...rest }) => (
    <IcBaselineTrendingUp
      className={cx(className, {
        'text-purple-500': active,
      })}
      {...rest}
    />
  ),
  [OrderOption.newest]: ({ active, className, ...rest }) => (
    <MdiSortAscending
      className={cx(className, {
        'text-emerald-500': active,
      })}
      {...rest}
    />
  ),
  [OrderOption.oldest]: ({ active, className, ...rest }) => (
    <MdiSortDescending
      className={cx(className, {
        'text-emerald-500': active,
      })}
      {...rest}
    />
  ),
  [OrderOption.mostUsed]: ({ active, className, ...rest }) => (
    <IcBaselineLocalFireDepartment
      className={cx(className, {
        'text-orange-300': active,
      })}
      {...rest}
    />
  ),
  [GenerationModelType.Lora]: ({ active, className, ...rest }) => (
    <IcBaselineAutoAwesome
      className={cx(className, {
        'text-purple-600': active,
      })}
      {...rest}
    />
  ),
  [GenerationModelType.SdV1Model]: ({ active, className, ...rest }) => (
    <IcBaselineSmartToy
      className={cx(className, {
        'text-purple-600': active,
      })}
      {...rest}
    />
  ),
  [OrderOption.trending]: ({ active, className, ...rest }) => (
    <IcBaselineTrendingUp
      className={cx(className, {
        'text-purple-500': active,
      })}
      {...rest}
    />
  ),
  [OrderOption.recommended]: ({ active, className, ...rest }) => (
    <IcBaselineAssistant
      className={cx(className, {
        'text-purple-500': active,
      })}
      {...rest}
    />
  ),
}

export const useOrderState = (choices: string[] = defaultChoices) => {
  const defaultValue = choices[0]
  const [{ sort: querySort }] = useQs()
  const [hash, setHash] = useHash()

  // Parse sort value from hash (format: "sort=value")
  const hashSort = new URLSearchParams(hash.replace(/^#/, '')).get('sort')

  // Migrate from query param to hash param
  useEffect(() => {
    if (querySort) {
      const url = new URL(window.location.href)
      url.searchParams.delete('sort')
      // Merge sort into existing hash params (preserving q and others)
      const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
      hashParams.set('sort', querySort)
      // Set hash on url before replaceState so the single history entry has the final URL
      url.hash = hashParams.toString()
      window.history.replaceState(null, '', url.toString())
      // Sync hook state to the same value (no additional history entry)
      setHash(hashParams.toString())
    }
  }, [querySort, setHash])

  // Priority: querySort (during migration) > hashSort > defaultValue
  const value = querySort || hashSort || defaultValue
  const setValue = (value: string) => {
    const hashParams = new URLSearchParams(hash.replace(/^#/, ''))
    hashParams.set('sort', value)
    setHash(hashParams.toString())
  }
  return [value, setValue] as const
}

export type OrderSelectProps = {
  choices: { value: string; label: ReactNode }[]
  value?: string | undefined
  onChange?: (value: string) => any
  alwaysCollapse?: boolean

  disabled?: boolean
  className?: string
  /**
   * Disable MUI Menu's portal to workaround modal conflicts
   *
   * @deprecated No longer used
   */
  disablePortal?: boolean
}

export default function OrderSelect({
  choices,
  value,
  onChange,
  alwaysCollapse = false,
  className,
  disabled,
}: OrderSelectProps) {
  const [{ tag }] = useQs()
  const location = useLocation()
  const { selectedContestTag } = useArtworkListPageContext()

  const items = useMemo(
    () =>
      choices
        .filter(({ value: choiceValue }) => {
          const isAll = tag === 'all' || !tag
          // Raven said this
          if (
            location.pathname === '/' &&
            isAll &&
            choiceValue === OrderOption.oldest
          ) {
            return false
          }
          if (selectedContestTag) {
            if ([OrderOption.followingNews].includes(choiceValue)) return false
            if (selectedContestTag.isEnded) {
              if (
                ![
                  OrderOption.mostLiked,
                  OrderOption.mostUsed,
                  OrderOption.newest,
                  OrderOption.oldest,
                ].includes(choiceValue)
              ) {
                return false
              }
            }
          }
          return true
        })
        .map(it => ({
          value: it.value,
          label: (
            <>
              {orderIconMap[it.value]?.({
                active: it.value === value,
                className: 'size-6',
              })}
              <span className="ml-1">{it.label}</span>
            </>
          ),
        })),
    [tag, location, selectedContestTag, value, choices],
  )

  // Select the first item by default
  if (
    onChange &&
    (!value ||
      (value &&
        !items.find(c => c.value === value) &&
        value !== items[0].value))
  ) {
    onChange(items[0].value)
  }

  return alwaysCollapse ? (
    <FeedSelect
      items={items}
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled}
      className={className}
    />
  ) : (
    <TabbedSelect
      items={items}
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled}
      className={className}
    />
  )
}

/*
 * =============================================================================
 */

const feedSelectItemShape = tv({
  base: [
    'h-10 px-3 flex items-center text-neutral-700 dark:text-neutral-200 rounded-lg',
    'group/item',
  ],
  variants: {
    selected: {
      true: [
        'bg-zinc-200/80 hover:bg-zinc-300/80',
        'dark:bg-zinc-200/20 dark:hover:bg-zinc-200/30',
      ],
      false: 'hover:bg-zinc-100/80 dark:hover:bg-zinc-100/10',
    },
    disabled: {
      true: 'cursor-default',
      false: 'cursor-pointer',
    },
  },
  compoundVariants: [
    {
      disabled: true,
      selected: false,
      className: 'text-neutral-400 dark:text-neutral-500',
    },
  ],
})

export type FeedSelectProps<T extends Key> = StyleProps & {
  items: Array<{ value: T; label?: ReactNode }>
  value?: T
  onChange?: (value: T) => void
  disabled?: boolean
}

export function FeedSelect<T extends Key>({
  items,
  value,
  onChange,
  disabled,
  className,
  style,
}: FeedSelectProps<T>) {
  return (
    <AriaSelect
      selectedKey={value}
      onSelectionChange={value => onChange?.(value as T)}
      isDisabled={disabled}
      // Should use something like "Feed/Order" though. But, whatever.
      aria-label={extractTextFromNode(
        items.find(it => it.value === value)?.label ?? '',
      )}
      className={cn('inline-block', className)}
      style={style}
    >
      <AriaButton
        className={({ isDisabled }) =>
          feedToolbarButtonStyles({
            disabled: isDisabled,
            className: 'group/trigger',
          })
        }
      >
        <AriaSelectValue className="contents" />
        <IconParkOutlineDown className="ml-2 group-pressed/trigger:-scale-y-100" />
      </AriaButton>
      <AriaPopover
        offset={4}
        crossOffset={-4}
        className={cn(
          'box-content p-1 text-sm bg-background dark:bg-background-secondary rounded-xl shadow-xl ring-1 ring-black/10',
          'transition ease-out',
          'entering:opacity-0 data-[placement=bottom]:entering:-translate-y-1 data-[placement=top]:entering:translate-y-1 entering:duration-200',
          'exiting:opacity-0 data-[placement=bottom]:exiting:-translate-y-1 data-[placement=top]:exiting:translate-y-1 exiting:duration-150',
        )}
        style={{ minWidth: 'var(--trigger-width)' }}
      >
        <AriaListBox items={items} className="flex flex-col gap-0.5">
          {item => (
            <AriaListBoxItem
              id={item.value}
              textValue={extractTextFromNode(item.label)}
              className={({ isSelected, isDisabled }) =>
                feedSelectItemShape({
                  selected: isSelected,
                  disabled: isDisabled,
                  className: 'gap-2',
                })
              }
            >
              {item.label === undefined ? item.value : item.label}
            </AriaListBoxItem>
          )}
        </AriaListBox>
      </AriaPopover>
    </AriaSelect>
  )
}

const tabbedSelectTabShape = tv({
  base: [
    'h-10 shrink-0 whitespace-nowrap px-4 py-2 flex items-center text-neutral-700 dark:text-neutral-200 rounded-full',
    'group/tab',
  ],
  variants: {
    selected: {
      true: [
        'bg-zinc-200/80 hover:bg-zinc-300/80',
        'dark:bg-zinc-200/20 dark:hover:bg-zinc-200/30',
      ],
      false: 'hover:bg-zinc-100/80 dark:hover:bg-zinc-100/10',
    },
    disabled: {
      true: 'cursor-default',
      false: 'cursor-pointer',
    },
  },
  compoundVariants: [
    {
      disabled: true,
      selected: false,
      className: 'text-neutral-400 dark:text-neutral-500',
    },
  ],
})

export type TabbedSelectProps<T extends Key> = StyleProps & {
  items: Array<{ value: T; label?: ReactNode }>
  value?: T
  onChange?: (value: T) => void
  disabled?: boolean
}

export function TabbedSelect<T extends Key>({
  items,
  value,
  onChange,
  disabled,
  className,
  style,
}: TabbedSelectProps<T>) {
  return (
    <AriaTabs
      selectedKey={value}
      onSelectionChange={value => onChange?.(value as T)}
      isDisabled={disabled}
      className={cn('block max-w-full min-w-0', className)}
      style={style}
    >
      <AriaTabList
        items={items}
        className="flex max-w-full gap-px overflow-x-auto hide-scrollbar"
      >
        {item => (
          <AriaTab
            id={item.value}
            className={({ isSelected, isDisabled }) =>
              tabbedSelectTabShape({
                selected: isSelected,
                disabled: isDisabled,
              })
            }
          >
            {item.label === undefined ? item.value : item.label}
          </AriaTab>
        )}
      </AriaTabList>
    </AriaTabs>
  )
}
